import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '../utils/api';

// Hardcoded stories (always available)
const STATIC_STORIES = [
  {
    id: 'banarasi-weaving', title: 'The Art of Banarasi Weaving', date: 'January 15, 2026',
    author: 'Vastram Team', category: 'Heritage',
    image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800',
    excerpt: 'Discover the centuries-old tradition of Banarasi silk weaving and the artisans who keep it alive.',
    tags: ['heritage', 'banarasi', 'weaving'],
  },
  {
    id: 'choosing-perfect-saree', title: 'Choosing the Perfect Saree', date: 'January 10, 2026',
    author: 'Priya Sharma', category: 'Style Guide',
    image: 'https://images.unsplash.com/photo-1739429945851-cf348b087e3d?w=800',
    excerpt: 'A comprehensive guide to selecting the right saree for every occasion and body type.',
    tags: ['style guide', 'fashion', 'tips'],
  },
  {
    id: 'saree-draping-styles', title: 'Saree Draping Styles Across India', date: 'January 5, 2026',
    author: 'Meera Reddy', category: 'Tutorial',
    image: 'https://images.unsplash.com/photo-1739429944341-db4eefcd230a?w=800',
    excerpt: 'Explore different regional draping styles and find the one that suits you best.',
    tags: ['tutorial', 'draping', 'regional'],
  },
];

const CATEGORIES = ['All', 'Heritage', 'Style Guide', 'Tutorial'];

const AllStories = () => {
  const navigate = useNavigate();
  const [dbStories, setDbStories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  useEffect(() => {
    api.get('/drape-stories').then(r => setDbStories(r.data || [])).catch(() => {});
  }, []);

  // Merge static + DB stories, avoid duplicates by title
  const allStories = [...STATIC_STORIES];
  const staticTitles = new Set(STATIC_STORIES.map(s => s.title.toLowerCase()));
  for (const s of dbStories) {
    if (!staticTitles.has(s.title.toLowerCase())) {
      allStories.push({
        id: s.story_id,
        title: s.title,
        date: s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
        author: s.author || 'Vastram',
        category: (s.tags && s.tags[0]) || 'General',
        image: s.image_url || '',
        excerpt: s.excerpt || s.content?.substring(0, 150) || '',
        tags: s.tags || [],
        isDb: true,
      });
    }
  }

  // Collect all unique categories
  const categories = ['All', ...new Set(allStories.map(s => s.category).filter(Boolean))];

  // Filter
  const filtered = allStories.filter(s => {
    const matchesSearch = !search || s.title.toLowerCase().includes(search.toLowerCase()) ||
      s.excerpt.toLowerCase().includes(search.toLowerCase()) ||
      s.author.toLowerCase().includes(search.toLowerCase()) ||
      (s.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = categoryFilter === 'All' || s.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleStoryClick = (story) => {
    if (story.isDb) {
      navigate(`/drape-stories/${story.id}`);
    } else {
      navigate(`/drape-stories/${story.id}`);
    }
  };

  return (
    <div className="min-h-screen" data-testid="all-stories-page">
      {/* Header */}
      <section className="bg-forest py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="font-heading text-4xl sm:text-5xl text-ivory mb-4 tracking-tight">
            All <em className="italic">Stories</em>
          </h1>
          <p className="text-ivory/70 mb-8">Explore our collection of articles about sarees, tradition, and style</p>

          {/* Search */}
          <div className="max-w-lg mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/50" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search stories by title, author, or topic..."
              className="w-full pl-11 pr-4 py-3 bg-ivory text-forest text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-gold"
              data-testid="stories-search-input"
            />
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Category Chips */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${categoryFilter === cat ? 'bg-forest text-ivory border-forest' : 'bg-transparent text-forest border-gold/40 hover:bg-gold/10'}`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Results Count */}
          <p className="text-sm text-muted-foreground mb-6">{filtered.length} {filtered.length === 1 ? 'story' : 'stories'} found</p>

          {/* Stories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(story => (
              <div
                key={story.id}
                onClick={() => handleStoryClick(story)}
                className="cursor-pointer group bg-neutral-warm rounded-sm overflow-hidden hover:shadow-lg transition-shadow"
                data-testid="story-card"
              >
                {story.image && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={story.image} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                )}
                <div className="p-5">
                  <span className="text-xs uppercase tracking-[0.2em] text-gold">{story.category}</span>
                  <h3 className="font-heading text-xl text-forest mt-1 mb-2 group-hover:text-gold transition-colors">{story.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-3">{story.excerpt}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-gold">{story.author}</span>
                    <span className="text-xs text-muted-foreground">{story.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">No stories match your search.</p>
              <button onClick={() => { setSearch(''); setCategoryFilter('All'); }} className="mt-4 text-sm text-forest hover:text-gold transition-colors underline">
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AllStories;
